import { ReactNode, useEffect, useCallback, FC, useState } from 'react';
import { createPortal } from 'react-dom';
import cx from 'classnames';
import Button from '../Button/Button';
import styles from './Drawer.module.css';

export type DrawerPosition = 'top' | 'right' | 'bottom' | 'left';

export type DrawerProps = {
  'isOpen': boolean;
  'onClose': VoidFunction;
  'title': string;
  'children': ReactNode;
  'position'?: DrawerPosition;
  'className'?: string;
  'contentClassName'?: string;
  'headerClassName'?: string;
  'footerContent'?: ReactNode;
  'preventBackdropClose'?: boolean;
  'disablePortal'?: boolean;
  'showCloseButton'?: boolean;
  'hideButtonLabel'?: string;
  'data-testid'?: string;
};

export const DRAWER_CLOSE_ANIMATION_DURATION_MS = 200;

const Drawer: FC<DrawerProps> = ({
  isOpen,
  onClose,
  title,
  children,
  position = 'top',
  className,
  contentClassName,
  headerClassName,
  footerContent,
  preventBackdropClose = false,
  disablePortal = false,
  showCloseButton = true,
  hideButtonLabel = 'Hide',
  'data-testid': dataTestId = 'drawer',
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(isOpen);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setIsClosing(false);
    } else if (!isOpen && shouldRender) {
      setIsClosing(true);
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, DRAWER_CLOSE_ANIMATION_DURATION_MS);
      return () => clearTimeout(timer);
    }
  }, [isOpen, shouldRender]);

  useEffect(() => {
    if (!isOpen) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setShouldRender(false);
    }, DRAWER_CLOSE_ANIMATION_DURATION_MS);
  }, [onClose]);

  const handleBackdropClick = useCallback(() => {
    if (!preventBackdropClose) {
      handleClose();
    }
  }, [preventBackdropClose, handleClose]);

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleClose]);

  if (!shouldRender) return null;

  const drawer = (
    <>
      <div
        className={cx(styles.backdrop, isClosing && styles.closing)}
        onClick={handleBackdropClick}
        data-testid={`${dataTestId}-backdrop`}
      />
      <div
        className={cx(styles.drawer, styles[`drawer--${position}`], isClosing && styles.closing, className)}
        data-testid={dataTestId}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${dataTestId}-title`}
      >
        <div className={styles.filterHeaderWrapper}>
          <div className={cx(styles.filterHeader, headerClassName)}>
            <div className={styles.headline} id={`${dataTestId}-title`}>
              {title}
            </div>
            {showCloseButton && (
              <Button
                variant="text"
                size="M"
                color="default"
                onClick={handleClose}
                aria-label={`Hide ${title.toLowerCase()}`}
              >
                {hideButtonLabel}
              </Button>
            )}
          </div>
        </div>

        <div className={cx(styles.drawerContent, contentClassName)}>{children}</div>

        {footerContent && (
          <div className={styles.drawerFooter} data-testid={`${dataTestId}-footer`}>
            {footerContent}
          </div>
        )}
      </div>
    </>
  );

  return disablePortal ? drawer : createPortal(drawer, document.body);
};

export default Drawer;
